"use client";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  QuestionMarkCircleIcon, 
  ChevronDownIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CogIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export default function FAQ() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: isMobile ? 0.3 : 0.5, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: isMobile ? 0.95 : 0.8 },
    show: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const slideInFromLeft = {
    hidden: { opacity: 0, x: isMobile ? -20 : -50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const slideInFromRight = {
    hidden: { opacity: 0, x: isMobile ? 20 : 50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.15
      }
    }
  };

  const faqs = [
    {
      icon: UserGroupIcon,
      question: "ConnectRH remplace-t-il totalement mon équipe RH ?",
      answer: "Non, ConnectRH est conçu pour être un assistant, pas un remplaçant. Il automatise les réponses aux questions simples et répétitives, permettant à votre équipe RH de se concentrer sur des missions à plus forte valeur ajoutée : stratégie, accompagnement personnalisé, gestion des talents, etc.",
      gradient: "from-purple-500 to-pink-500",
      bgGradient: "from-purple-50 to-pink-50",
      borderColor: "border-purple-200"
    },
    {
      icon: ShieldCheckIcon,
      question: "Est-ce que mes données sont en sécurité avec ConnectRH ?",
      answer: "Absolument. ConnectRH respecte les plus hauts standards de sécurité. Vos données RH sont chiffrées, stockées de manière sécurisée et ne sont jamais partagées avec des tiers. Nous sommes conformes aux réglementations RGPD et suivons les meilleures pratiques en matière de protection des données.",
      gradient: "from-green-500 to-emerald-500",
      bgGradient: "from-green-50 to-emerald-50",
      borderColor: "border-green-200"
    },
    {
      icon: CogIcon,
      question: "ConnectRH peut-il s'adapter aux spécificités de mon entreprise ?",
      answer: "Oui, c'est l'un des points forts de ConnectRH. L'assistant peut être configuré avec les politiques, procédures et informations spécifiques à votre entreprise. Il apprend de votre base de connaissances RH pour fournir des réponses parfaitement adaptées à votre contexte organisationnel.",
      gradient: "from-blue-500 to-cyan-500",
      bgGradient: "from-blue-50 to-cyan-50",
      borderColor: "border-blue-200"
    },
    {
      icon: QuestionMarkCircleIcon,
      question: "Combien de temps faut-il pour mettre en place ConnectRH ?",
      answer: "L'implémentation de ConnectRH est ultra-rapide : en quelques étapes simples, vous êtes prêt à automatiser la gestion des questions RH. Aucune compétence technique avancée n'est requise : notre solution est clé en main, intégrée à vos outils existants, et opérationnelle en un temps record. Vous pouvez ainsi concentrer vos équipes RH sur l'essentiel sans attendre.",
      gradient: "from-orange-500 to-red-500",
      bgGradient: "from-orange-50 to-red-50",
      borderColor: "border-orange-200"
    },
    {
      icon: UserGroupIcon,
      question: "Quels bénéfices concrets apporte ConnectRH à mon entreprise ?",
      answer: "ConnectRH réduit le temps passé sur les tâches administratives répétitives, améliore l'expérience collaborateur, et permet aux équipes RH de se concentrer sur des projets stratégiques.",
      gradient: "from-indigo-500 to-purple-500",
      bgGradient: "from-indigo-50 to-purple-50",
      borderColor: "border-indigo-200"
    }
  ];

  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section id="faq" className={`py-16 bg-gradient-to-br from-gray-50 via-blue-50/30 to-violet-50/30 relative overflow-hidden smooth-scroll-section ${isMobile ? 'mobile-simple' : ''}`}>
      {/* Éléments décoratifs animés - désactivés sur mobile */}
      {!isMobile && (
        <>
          {/* Cercles décoratifs supprimés */}
        </>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: isMobile ? 0.05 : 0.1, margin: isMobile ? "-50px" : "-100px" }}
          className="text-center"
        >
          {/* Badge section */}
          <motion.div
            variants={scaleIn}
            whileHover={{ scale: 1.05 }}
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-violet-50 to-purple-50 border-2 border-violet-200 rounded-full px-6 py-3 mb-8 shadow-lg backdrop-blur-sm"
          >
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-6 h-6 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full flex items-center justify-center"
            >
              <QuestionMarkCircleIcon className="w-4 h-4 text-white" />
            </motion.div>
            <span className="text-sm font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              Questions fréquentes
            </span>
          </motion.div>

          {/* Titre principal */}
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-relaxed py-4"
            style={{ lineHeight: '1.3' }}
          >
            <motion.span
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "linear" 
              }}
              className="inline-block bg-gradient-to-r from-gray-900 via-violet-600 to-gray-900 bg-[length:200%_100%] bg-clip-text text-transparent"
            >
              Vos questions,
            </motion.span>
            <span className="block bg-gradient-to-r from-violet-600 to-purple-600 gradient-text-safe mt-2">
              nos réponses
            </span>
          </motion.h2>

          {/* Description */}
          <motion.div
            variants={fadeInUp}
            className="mb-16"
          >
            <p className="text-xl text-gray-600 leading-relaxed">
              Découvrez les réponses aux{" "}
              <span className="font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                questions les plus courantes
              </span>{" "}
              sur ConnectRH.
            </p>
          </motion.div>

          {/* Layout en deux colonnes : Contact à gauche, FAQ à droite */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 text-left">
            {/* Bloc de contact - Colonne de gauche */}
            <motion.div
              variants={slideInFromLeft}
              className="lg:col-span-1"
            >
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-3xl p-12 border-2 border-violet-200 h-fit sticky top-8 shadow-lg relative overflow-hidden group">
                {/* Effet de brillance au survol */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    Vous avez d'autres questions ?
                  </h3>
                  <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                    Notre équipe est là pour vous accompagner et répondre à toutes vos questions sur ConnectRH. 
                    Contactez-nous pour une consultation personnalisée et découvrez comment nous pouvons transformer 
                    votre gestion RH.
                  </p>
                  <motion.a
                    href="/contact"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center space-x-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-8 py-5 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 w-full justify-center text-lg"
                  >
                    <EnvelopeIcon className="w-6 h-6" />
                    <span>Nous contacter</span>
                  </motion.a>
                  
                  {/* Informations de contact supplémentaires */}
                  <div className="mt-12 pt-10 border-t border-violet-200">
                    <div className="space-y-8">
                      <motion.div 
                        whileHover={{ x: 5 }}
                        className="flex items-center space-x-4"
                      >
                        <div className="w-12 h-12 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                          <EnvelopeIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-base font-medium text-gray-900">Email</p>
                          <p className="text-base text-gray-600">contact@connectrh.fr</p>
                        </div>
                      </motion.div>
                      <motion.div 
                        whileHover={{ x: 5 }}
                        className="flex items-center space-x-4"
                      >
                        <div className="w-12 h-12 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                          <PhoneIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-base font-medium text-gray-900">Téléphone</p>
                          <p className="text-base text-gray-600">+33 1 23 45 67 89</p>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Questions FAQ - Colonne de droite */}
            <motion.div
              variants={staggerContainer}
              className="lg:col-span-1 space-y-4"
            >
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  whileHover={{ scale: 1.02 }}
                  className={`bg-gradient-to-br ${faq.bgGradient} rounded-3xl border-2 ${faq.borderColor} shadow-lg overflow-hidden relative group`}
                >
                  {/* Effet de brillance au survol */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-white/30 transition-colors duration-300 relative"
                  >
                    <div className="flex items-center space-x-4">
                      <motion.div
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                        className={`w-12 h-12 bg-gradient-to-r ${faq.gradient} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}
                      >
                        <faq.icon className="w-6 h-6 text-white" />
                      </motion.div>
                      <h3 className="text-lg font-bold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                    </div>
                    <motion.div
                      animate={{ rotate: activeIndex === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex-shrink-0"
                    >
                      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    </motion.div>
                  </button>
                  
                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 relative">
                          <div className="border-t border-gray-200/50 pt-6 ml-16">
                            <p className="text-gray-600 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
